import { useState, useEffect } from 'react'
import firebase from './firebase'

/**
 * Hook to fetch and provide market filter configurations from Firestore
 */
export const useMarketFilters = () => {
  const [marketFilters, setMarketFilters] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchMarketFilters = async () => {
      setLoading(true)
      try {
        // Reference to the marketFilters collection in Firestore
        // Get documents created through the Admin panel with resource name='admin/marketfilters'
        const marketFiltersRef = firebase.firestore().collection('marketfilters')
        
        // Set up real-time listener for filter changes
        const unsubscribe = marketFiltersRef.onSnapshot((snapshot) => {
          try {
            const filters = []
            snapshot.forEach(doc => {
              filters.push({
                id: doc.id,
                ...doc.data()
              })
            })
            
            setMarketFilters(filters)
            setLoading(false)
          } catch (parseError) {
            console.error('Error parsing market filters:', parseError)
            setError(parseError)
            setLoading(false)
          }
        }, (err) => {
          console.error("Error getting market filters:", err)
          setError(err)
          setLoading(false)
        })

        // Clean up listener on unmount
        return () => unsubscribe()
      } catch (err) {
        console.error("Error in useMarketFilters hook:", err)
        setError(err)
        setLoading(false)
      }
    }

    fetchMarketFilters()
  }, [])

  /**
   * Check if a specific filter is enabled
   * @param {string} filterId - Internal ID of the filter
   * @returns {boolean} - Whether the filter is enabled
   */
  const isFilterEnabled = (filterId) => {
    const filter = marketFilters.find(f => f.id === filterId)
    return filter?.enabled === true
  }

  /**
   * Get the label for a specific filter
   * @param {string} filterId - Internal ID of the filter
   * @param {string} fallbackLabel - Fallback label if no filter found
   * @returns {string} - The configured label or the fallback
   */
  const getFilterLabel = (filterId, fallbackLabel) => {
    const filter = marketFilters.find(f => f.id === filterId)
    
    // Return the label from Firestore if available
    if (filter?.label) {
      return filter.label
    }
    
    // Fall back to the provided fallback label
    return fallbackLabel || filterId
  }

  /**
   * Get all enabled filters
   * @returns {Array} - Array containing all enabled filter objects
   */
  const getEnabledFilters = () => {
    return marketFilters.filter(filter => filter.enabled === true)
  }

  return { 
    marketFilters,
    loading, 
    error,
    isFilterEnabled,
    getFilterLabel,
    getEnabledFilters
  }
}

export default useMarketFilters
